import { createContext, useContext } from 'react';

/**
 * FREMDZUGRIFF — der Begruendungsweg fuer Komponenten tief im Projektbaum.
 *
 * Die Stellen, an denen ein fremdes Projekt nach einer Begruendung verlangt
 * (Begehung anlegen, Leistung buchen — useReportActions unter ProjectHub),
 * haengen mehrere Ebenen unter App.tsx. Eine Prop dorthin fuehrte durch
 * ProjectHub, und die Datei ist gerade in fremder Hand (Projektstand-Karte,
 * nicht committet). Deshalb ein Kontext wie beim Lernzentrum-Oeffner: App.tsx
 * stellt ihn einmal um den ganzen Baum, der Hook holt ihn sich selbst.
 *
 * Ohne Provider ist er stumm: `pending` false und eine Anfrage, die sofort mit
 * false endet. Ein Test oder eine Vorschau ohne App soll nichts werfen und
 * genau das tun, was ohne Fremdzugriff ohnehin geschieht.
 */
export interface ForeignAccessContextValue {
    /**
     * Fremdes Projekt, fuer heute noch nicht begruendet — offen zum Ansehen.
     * Nur dann lohnt die Anfrage; sonst ist der Lesemodus ein anderer (Sperre,
     * ausdruecklicher Schreibschutz) und hat seine eigene Absage.
     */
    pending: boolean;
    /**
     * Oeffnet den Begruendungsdialog. true = begruendet, Sperre genommen und
     * frisch geladen: ab jetzt wird geschrieben. false = „Nur ansehen",
     * gescheitert oder die Sperre haelt jemand anderes.
     *
     * Wer diese Zusage abwartet, darf danach NICHT mit Werten aus dem Render
     * vor der Anfrage weiterschreiben — das Projekt wurde dazwischen neu
     * geladen.
     */
    requestJustification: () => Promise<boolean>;
}

export const FOREIGN_ACCESS_OHNE_PROVIDER: ForeignAccessContextValue = {
    pending: false,
    requestJustification: () => Promise.resolve(false),
};

export const ForeignAccessContext = createContext<ForeignAccessContextValue>(FOREIGN_ACCESS_OHNE_PROVIDER);

export const useForeignAccess = (): ForeignAccessContextValue => useContext(ForeignAccessContext);
